import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiService, VolatilityRange, VolatilityOpportunitiesResponse } from '../services/api';
import { Refresh } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const VolatilityRanges: React.FC = () => {
  const theme = useTheme();
  const [symbol, setSymbol] = useState('');
  const [duration, setDuration] = useState('6 M');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rangeData, setRangeData] = useState<VolatilityRange | null>(null);

  // Top opportunities state
  const [buyOpportunities, setBuyOpportunities] = useState<VolatilityOpportunitiesResponse | null>(null);
  const [sellOpportunities, setSellOpportunities] = useState<VolatilityOpportunitiesResponse | null>(null);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [opportunitiesError, setOpportunitiesError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  // Low range scan state
  const [lowRangeData, setLowRangeData] = useState<any | null>(null);
  const [lowRangeLoading, setLowRangeLoading] = useState(false);
  const [lowRangeError, setLowRangeError] = useState<string | null>(null);
  const [lowRangeMeta, setLowRangeMeta] = useState<Record<string, { name: string; sector: string }>>({});
  const [lowRangeFilter, setLowRangeFilter] = useState('');
  const [fullScanRunning, setFullScanRunning] = useState(false);
  const [fullScanStatus, setFullScanStatus] = useState<string | null>(null);

  const fetchOpportunities = async () => {
    setOpportunitiesLoading(true);
    setOpportunitiesError(null);
    try {
      const [buyData, sellData] = await Promise.all([
        apiService.getVolatilityOpportunities('BUY', 10),
        apiService.getVolatilityOpportunities('SELL', 10),
      ]);
      setBuyOpportunities(buyData);
      setSellOpportunities(sellData);
    } catch (err: any) {
      setOpportunitiesError(err.response?.data?.detail || 'Failed to fetch opportunities');
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  useEffect(() => { fetchOpportunities(); }, []);

  const handleQuickScan = async () => {
    setScanning(true);
    setOpportunitiesError(null);
    try {
      await apiService.runVolatilityQuickScan(100);
      await fetchOpportunities();
    } catch (err: any) {
      setOpportunitiesError(err.response?.data?.detail || 'Quick scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleLowRangeScan = async () => {
    setLowRangeLoading(true);
    setLowRangeError(null);
    try {
      const data = await apiService.getLowRangeStocks();
      setLowRangeData(data);
      if (data.stocks?.length) {
        const symbols = data.stocks.map((s: any) => s.symbol);
        // Fetch in chunks of 50 (API limit)
        for (let i = 0; i < symbols.length; i += 50) {
          apiService.getTickerInfo(symbols.slice(i, i + 50)).then(info => {
            setLowRangeMeta(prev => ({ ...prev, ...info }));
          }).catch(() => {});
        }
      }
    } catch (err: any) {
      setLowRangeError(err.response?.data?.detail || 'Failed to fetch low range stocks');
    } finally {
      setLowRangeLoading(false);
    }
  };

  const handleRunFullScan = async () => {
    setFullScanRunning(true);
    setFullScanStatus(null);
    try {
      await apiService.triggerVolatilityScan();
      setFullScanStatus('Full scan started — scanning all NASDAQ + NYSE stocks (~12,000). Takes 30–60 min. Results will appear here when done.');
    } catch (err: any) {
      setFullScanStatus('Failed to start scan: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFullScanRunning(false);
    }
  };

  const handleGetRanges = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiService.getTradingRanges(symbol.toUpperCase(), duration, 'all');
      setRangeData(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch ranges');
    } finally {
      setLoading(false);
    }
  };

  const getZoneColor = (zone: string) => {
    if (zone === 'STRONG_BUY' || zone === 'BUY') return theme.palette.success.main;
    if (zone === 'STRONG_SELL' || zone === 'SELL') return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const getZoneIcon = (zone: string) => {
    if (zone === 'STRONG_BUY' || zone === 'BUY') return <TrendingUp />;
    if (zone === 'STRONG_SELL' || zone === 'SELL') return <TrendingDown />;
    return <TrendingFlat />;
  };


  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Volatility Range Prediction
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Predict price ranges for automated buy low / sell high strategies
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stock Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleGetRanges()}
                placeholder="AAPL"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Lookback Period</InputLabel>
                <Select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  label="Lookback Period"
                >
                  <MenuItem value="1 M">1 Month</MenuItem>
                  <MenuItem value="3 M">3 Months</MenuItem>
                  <MenuItem value="6 M">6 Months</MenuItem>
                  <MenuItem value="1 Y">1 Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGetRanges}
                disabled={loading || !symbol}
                sx={{ height: '56px' }}
              >
                Get Trading Ranges
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Top Opportunities Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Top NASDAQ Opportunities
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleQuickScan}
              disabled={scanning || opportunitiesLoading}
              startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <TrendingUp />}
            >
              {scanning ? 'Scanning 100 stocks…' : 'Quick Scan (100 stocks)'}
            </Button>
            <Button
              variant="outlined"
              onClick={fetchOpportunities}
              disabled={scanning || opportunitiesLoading}
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {buyOpportunities?.count || sellOpportunities?.count
            ? `Latest scan: ${buyOpportunities?.count || 0} BUY, ${sellOpportunities?.count || 0} SELL opportunities`
            : 'No scan data for today. Run a Quick Scan to find opportunities across the top 100 NASDAQ stocks.'}
        </Typography>

        {opportunitiesLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
        {opportunitiesError && <Alert severity="error" sx={{ mb: 3 }}>{opportunitiesError}</Alert>}

        {!opportunitiesLoading && !opportunitiesError && (
          <Grid container spacing={3}>
            {/* Buy Opportunities */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp color="success" />
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      Top 10 Buy Opportunities
                    </Typography>
                  </Box>
                  {buyOpportunities && buyOpportunities.opportunities.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Position</TableCell>
                            <TableCell align="right">Confidence</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {buyOpportunities.opportunities.map((opp: any) => (
                            <TableRow key={opp.symbol} hover sx={{ cursor: 'pointer' }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {opp.symbol}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ${opp.current_price.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Range: ${opp.range.low.toFixed(2)} - ${opp.range.high.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${opp.position.in_range_pct.toFixed(1)}%`}
                                  size="small"
                                  color="success"
                                  sx={{ fontWeight: 600 }}
                                />
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {opp.position.from_low_pct.toFixed(1)}% from low
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${(opp.confidence * 100).toFixed(0)}%`}
                                  size="small"
                                  color={opp.confidence > 0.8 ? 'success' : 'warning'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No buy opportunities found in latest scan</Alert>
                  )}
                  {buyOpportunities && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      Scan Date: {buyOpportunities.scan_date}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Sell Opportunities */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingDown color="error" />
                    <Typography variant="h6" fontWeight={600} color="error.main">
                      Top 10 Sell Opportunities
                    </Typography>
                  </Box>
                  {sellOpportunities && sellOpportunities.opportunities.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Position</TableCell>
                            <TableCell align="right">Confidence</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sellOpportunities.opportunities.map((opp: any) => (
                            <TableRow key={opp.symbol} hover sx={{ cursor: 'pointer' }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {opp.symbol}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ${opp.current_price.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Range: ${opp.range.low.toFixed(2)} - ${opp.range.high.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${opp.position.in_range_pct.toFixed(1)}%`}
                                  size="small"
                                  color="error"
                                  sx={{ fontWeight: 600 }}
                                />
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {opp.position.from_high_pct.toFixed(1)}% from high
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${(opp.confidence * 100).toFixed(0)}%`}
                                  size="small"
                                  color={opp.confidence > 0.8 ? 'success' : 'warning'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No sell opportunities found in latest scan</Alert>
                  )}
                  {sellOpportunities && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      Scan Date: {sellOpportunities.scan_date}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* NASDAQ Low Range Stocks */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              NASDAQ + NYSE Low Range Stocks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All stocks currently near their range low (BUY zone) — sorted most oversold first. Hover a symbol for company info.
              Full scan (~12,000 stocks) runs daily at 4:30 PM ET.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="success"
              onClick={handleLowRangeScan}
              disabled={lowRangeLoading}
              startIcon={lowRangeLoading ? <CircularProgress size={16} color="inherit" /> : <TrendingUp />}
            >
              {lowRangeLoading ? 'Loading…' : 'Show Low Range Stocks'}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleRunFullScan}
              disabled={fullScanRunning}
              startIcon={fullScanRunning ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {fullScanRunning ? 'Starting…' : 'Run Full Scan'}
            </Button>
          </Box>
        </Box>

        {fullScanStatus && (
          <Alert severity={fullScanStatus.startsWith('Failed') ? 'error' : 'info'} sx={{ mb: 2 }} onClose={() => setFullScanStatus(null)}>
            {fullScanStatus}
          </Alert>
        )}
        {lowRangeError && <Alert severity="error" sx={{ mb: 2 }}>{lowRangeError}</Alert>}

        {lowRangeData && !lowRangeLoading && (
          lowRangeData.count === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No BUY-zone stocks found. The full NASDAQ + NYSE scan (~12,000 stocks) runs daily at 4:30 PM ET.
              Click <strong>Run Full Scan</strong> to trigger it now (takes 30–60 min).
            </Alert>
          ) : (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <TextField
                  size="small"
                  placeholder="Filter by ticker or name…"
                  value={lowRangeFilter}
                  onChange={(e) => setLowRangeFilter(e.target.value)}
                  sx={{ mb: 2, minWidth: 260 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {lowRangeData.count} stocks in BUY zone · Scan date: {lowRangeData.scan_date}
                  {lowRangeData.is_stale && (
                    <span style={{ color: '#ff9800', marginLeft: 6 }}>⚠ From previous scan — today's full scan hasn't run yet</span>
                  )}
                  {lowRangeData.range_basis && (
                    <> · <Tooltip title="Range is calculated as a 50-day Bollinger Band (±2σ) fitted on the last 1 year of daily prices. The band reflects recent price behaviour — roughly the last 2–3 months." placement="top" arrow>
                      <span style={{ cursor: 'help', borderBottom: '1px dashed' }}>{lowRangeData.range_basis}</span>
                    </Tooltip></>
                  )}
                </Typography>
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Range Low</TableCell>
                        <TableCell align="right">Range High</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Where price sits within [Range Low → Range High]. 0% = at range low, 100% = at range high. Negative = price has broken below the range (extremely oversold — verify it's not a crash)." placement="top" arrow>
                            <span style={{ cursor: 'help', borderBottom: '1px dashed' }}>Position in Range</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="How far the current price is below the range low. More negative = further below support." placement="top" arrow>
                            <span style={{ cursor: 'help', borderBottom: '1px dashed' }}>Below Range Low</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Bollinger Band width as % of price. Wider = more volatile, more meaningful signal. Stocks with <8% range (SPACs, trusts) are filtered out." placement="top" arrow>
                            <span style={{ cursor: 'help', borderBottom: '1px dashed' }}>Range Width</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">Confidence</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lowRangeData.stocks.filter((s: any) => {
                        const q = lowRangeFilter.trim().toUpperCase();
                        if (!q) return true;
                        const name = (lowRangeMeta[s.symbol]?.name || '').toUpperCase();
                        return s.symbol.includes(q) || name.includes(q);
                      }).map((s: any) => {
                        const belowRange = s.position_in_range < 0;
                        const deeplyBelow = s.position_in_range < -20;
                        return (
                        <TableRow key={s.symbol} hover>
                          <TableCell>
                            <Tooltip
                              title={lowRangeMeta[s.symbol]?.name
                                ? `${lowRangeMeta[s.symbol].name}${lowRangeMeta[s.symbol].sector ? ` · ${lowRangeMeta[s.symbol].sector}` : ''}`
                                : s.symbol}
                              placement="right"
                              arrow
                            >
                              <Typography variant="body2" fontWeight={600} color="success.main" sx={{ cursor: 'default', display: 'inline' }}>
                                {s.symbol}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">${s.current_price.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            ${s.range_low.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">${s.range_high.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={belowRange
                                ? deeplyBelow
                                  ? `Price is ${Math.abs(s.position_in_range).toFixed(0)}% of range width BELOW support. Verify this isn't a crash or delisting.`
                                  : `Price has broken slightly below the range low (oversold).`
                                : `Price is at ${s.position_in_range.toFixed(0)}% of the way from range low to range high.`}
                              placement="left"
                              arrow
                            >
                              <Chip
                                label={belowRange
                                  ? `${s.position_in_range.toFixed(1)}% ⚠`
                                  : `${s.position_in_range.toFixed(1)}%`}
                                size="small"
                                color={deeplyBelow ? 'warning' : 'success'}
                                sx={{ fontWeight: 600, cursor: 'help' }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color={belowRange ? 'warning.main' : 'success.main'}>
                              {s.distance_from_low.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color={
                              s.range_width_pct >= 20 ? 'success.main' :
                              s.range_width_pct >= 12 ? 'text.primary' : 'text.secondary'
                            }>
                              {s.range_width_pct != null ? `${s.range_width_pct.toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${(s.confidence * 100).toFixed(0)}%`}
                              size="small"
                              color={s.confidence > 0.8 ? 'success' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )
        )}
      </Box>

      {/* Individual Stock Search Results */}
      <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mt: 4, mb: 2 }}>
        Individual Stock Analysis
      </Typography>

      {/* Loading / Error */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Range Results */}
      {rangeData && !loading && (
        <>
          {/* Current Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">
                    {rangeData.symbol} Current Price
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    ${rangeData.current_price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">
                    Trading Zone
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getZoneIcon(rangeData.current_zone)}
                    <Chip
                      label={rangeData.current_zone.replace('_', ' ')}
                      size="medium"
                      sx={{
                        backgroundColor: getZoneColor(rangeData.current_zone) + '20',
                        color: getZoneColor(rangeData.current_zone),
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">
                    Recommendation
                  </Typography>
                  <Chip
                    label={rangeData.recommendation.action}
                    size="medium"
                    sx={{
                      backgroundColor: getZoneColor(rangeData.recommendation.action) + '20',
                      color: getZoneColor(rangeData.recommendation.action),
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Expected Return: {rangeData.recommendation.expected_return_pct.toFixed(2)}%
                  </Typography>
                </Grid>
              </Grid>

              {/* What testing says about this range, and the ranges that were tested forward */}
              {rangeData.page_test && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>This range is where the price has been, not where it will be.</strong> Tested on 300 stocks a
                  month for {rangeData.page_test.dates} months (2018–2026) with {rangeData.page_test.lookback} of history: a
                  week later the price was inside it {Math.round(rangeData.page_test['inside_at_1 week_pct'])}% of the time,
                  a month later {Math.round(rangeData.page_test['inside_at_1 month_pct'])}%.
                  {rangeData.page_test.zones?.[rangeData.current_zone] && (
                    <> Stocks in the {rangeData.current_zone.replace('_', ' ')} zone did{' '}
                      {rangeData.page_test.zones[rangeData.current_zone].next_month_vs_average_pct >= 0 ? '+' : ''}
                      {rangeData.page_test.zones[rangeData.current_zone].next_month_vs_average_pct.toFixed(1)}% against the
                      average stock the next month — no zone predicted the next month clearly, and the BUY zones did
                      slightly worse than the SELL zones. Read the zone as a position, not a signal.</>
                  )}
                </Alert>
              )}
              {rangeData.forward_ranges && rangeData.forward_ranges.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Where the price may be — ranges that were tested forward
                  </Typography>
                  <Table size="small" sx={{ maxWidth: 720, '& td, & th': { px: 1 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>In</TableCell>
                        <TableCell align="right">Likely range (8 in 10)</TableCell>
                        <TableCell align="right">From today</TableCell>
                        <TableCell align="right">Held in testing</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rangeData.forward_ranges.map(f => (
                        <TableRow key={f.horizon}>
                          <TableCell>{({ 5: '1 week', 21: '1 month', 63: '3 months' } as Record<number, string>)[f.horizon] ?? `${f.horizon} days`}</TableCell>
                          <TableCell align="right">${f.lower.toFixed(2)} – ${f.upper.toFixed(2)}</TableCell>
                          <TableCell align="right">{(f.lower_pct * 100).toFixed(1)}% to +{(f.upper_pct * 100).toFixed(1)}%</TableCell>
                          <TableCell align="right">{f.tested?.held != null ? `${Math.round(f.tested.held * 100)}%` : '–'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    From the past year's volatility, centred on today's price and stretched by how far it missed in a
                    month-by-month test since 2020 (ML Predictions, Feature 43). Aims to hold the price 8 times in 10;
                    "held" is how often it did. It says how far the price may move, not which way.
                  </Typography>
                </Box>
              )}

              {/* Visual Range Indicator */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Price Position in Range
                </Typography>
                {rangeData.ranges.combined && rangeData.actual_range_low != null && rangeData.actual_range_high != null && (() => {
                  const barLow = rangeData.actual_range_low!;
                  const barHigh = rangeData.actual_range_high!;
                  const barSpan = barHigh - barLow;
                  const zoneLowPct = Math.max(0, Math.min(100, ((rangeData.ranges.combined.lower - barLow) / barSpan) * 100));
                  const zoneHighPct = Math.max(0, Math.min(100, ((rangeData.ranges.combined.upper - barLow) / barSpan) * 100));
                  const pricePct = Math.max(0, Math.min(100, ((rangeData.current_price - barLow) / barSpan) * 100));
                  return (
                    <Box sx={{ position: 'relative', pt: 2, pb: 1 }}>
                      <Box
                        sx={{
                          height: 40,
                          background: `linear-gradient(to right,
                            ${theme.palette.success.main}40 0%,
                            ${theme.palette.success.main}40 ${zoneLowPct}%,
                            ${theme.palette.warning.main}40 ${zoneLowPct}%,
                            ${theme.palette.warning.main}40 ${zoneHighPct}%,
                            ${theme.palette.error.main}40 ${zoneHighPct}%,
                            ${theme.palette.error.main}40 100%)`,
                          borderRadius: 2,
                          position: 'relative',
                          overflow: 'visible',
                        }}
                      >
                        {/* Zone boundary: combined lower */}
                        <Box sx={{
                          position: 'absolute', left: `${zoneLowPct}%`, top: 0, bottom: 0,
                          width: 2, bgcolor: theme.palette.success.main, opacity: 0.7,
                        }} />
                        {/* Zone boundary: combined upper */}
                        <Box sx={{
                          position: 'absolute', left: `${zoneHighPct}%`, top: 0, bottom: 0,
                          width: 2, bgcolor: theme.palette.error.main, opacity: 0.7,
                        }} />
                        {/* Current price marker */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${pricePct}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 4,
                            height: 50,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                      {/* Endpoint labels: actual range */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          ${barLow.toFixed(2)} <Typography variant="caption" color="text.disabled">(period low)</Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          ${barHigh.toFixed(2)} <Typography variant="caption" color="text.disabled">(period high)</Typography>
                        </Typography>
                      </Box>
                      {/* Zone labels: combined lower / middle / upper */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="success.main" fontWeight={600}>
                          Buy zone ≤ ${rangeData.ranges.combined.lower.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="warning.main" fontWeight={600}>
                          Mid ${rangeData.ranges.combined.middle?.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="error.main" fontWeight={600}>
                          Sell zone ≥ ${rangeData.ranges.combined.upper.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                {rangeData.recommendation.reason}
              </Alert>
            </CardContent>
          </Card>

          {/* Historical Range Card */}
          {rangeData.actual_range_low && rangeData.actual_range_high && (
            <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                    Historical Range ({rangeData.lookback_period})
                  </Typography>
                  <Tooltip title="This shows the actual lowest and highest prices during the selected lookback period. The predicted trading ranges above are narrower because they reflect realistic near-term expectations.">
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Actual Low (Period)
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      ${rangeData.actual_range_low.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Actual High (Period)
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="error.main">
                      ${rangeData.actual_range_high.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Full Range Span
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {((rangeData.actual_range_high - rangeData.actual_range_low) / rangeData.actual_range_low * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Data Period: {rangeData.data_start_date} to {rangeData.data_end_date}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Timeframe Metadata */}
          {rangeData.lookback_period && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Analysis Timeframe
                    </Typography>
                    <Tooltip title="These ranges are calculated based on historical price patterns over the lookback period. The actual range shows the full price span during this period, while the tradeable range (shown above) represents the statistically expected boundaries.">
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Lookback Period
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {rangeData.lookback_period}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Recommendation Valid
                      </Typography>
                      <Chip
                        label={rangeData.recommendation_validity}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Range Methods Breakdown */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Range Calculation Methods
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Lower Bound</TableCell>
                      <TableCell align="right">Middle</TableCell>
                      <TableCell align="right">Upper Bound</TableCell>
                      <TableCell align="right">Confidence</TableCell>
                      <TableCell align="right">Range Width</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(rangeData.ranges).map(([method, range]: [string, any]) => (
                      <TableRow key={method} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {method.toUpperCase()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            ${range.lower.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {range.middle ? (
                            <Typography variant="body2" color="warning.main" fontWeight={600}>
                              ${range.middle.toFixed(2)}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main" fontWeight={600}>
                            ${range.upper.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${(range.confidence * 100).toFixed(0)}%`}
                            size="small"
                            color={range.confidence > 0.8 ? 'success' : range.confidence > 0.6 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          ${(range.upper - range.lower).toFixed(2)}
                          <Typography variant="caption" color="text.secondary" display="block">
                            ({(((range.upper - range.lower) / rangeData.current_price) * 100).toFixed(1)}%)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {rangeData.recommendation.target_price && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Trading Plan
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'success.main', color: 'success.contrastText' }}>
                        <Typography variant="caption">Target Price</Typography>
                        <Typography variant="h6" fontWeight={700}>
                          ${rangeData.recommendation.target_price.toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                    {rangeData.recommendation.stop_loss && (
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'error.main', color: 'error.contrastText' }}>
                          <Typography variant="caption">Stop Loss</Typography>
                          <Typography variant="h6" fontWeight={700}>
                            ${rangeData.recommendation.stop_loss.toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        <Typography variant="caption">Expected Return</Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {rangeData.recommendation.expected_return_pct.toFixed(2)}%
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default VolatilityRanges;
